import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    dataUrl: String,
  };

  connect() {
    this.retryCount = 0;
    this.maxRetries = 10;
    
    if (!this.dataUrlValue || this.dataUrlValue === "") {
      return;
    }

    this.loadPolyline();
  }

  async loadPolyline() {
    try {
      const coordinates = await this.fetchAndParseCoordinates();
      if (coordinates && coordinates.length > 0) {
        this.addPolylineToMap(coordinates);
      }
    } catch (error) {
      console.warn("Failed to load Route 66 polyline:", error);
    }
  }

  async fetchAndParseCoordinates() {
    const response = await fetch(this.dataUrlValue);
    if (!response.ok) {
      throw new Error(`Failed to fetch polyline data: ${response.status}`);
    }

    const data = await response.json();
    const features = data?.jsondata?.data?.features || [];

    // Only extract coordinates from LineString features with red stroke (#cc0000)
    // This is the primary Route 66 route - ignore all alternate routes
    let coordinates = [];
    features.forEach((feature) => {
      if (feature.geometry?.type !== "LineString") return;

      const strokeColor = feature.properties?.stroke;
      // Only include the red Route 66 route
      if (strokeColor !== "#cc0000") return;

      const featureCoords = feature.geometry.coordinates.map((coord) => {
        // GeoJSON format is [longitude, latitude], convert to {latitude, longitude}
        return { latitude: coord[1], longitude: coord[0] };
      });

      coordinates = coordinates.concat(featureCoords);
    });

    // Remove duplicates
    const uniqueCoords = coordinates.filter((coord, index, self) =>
      index === self.findIndex((c) => c.latitude === coord.latitude && c.longitude === coord.longitude)
    );

    return uniqueCoords;
  }

  addPolylineToMap(coordinates) {
    // Find the dynamic map component element (it's a child of this controller's element)
    const mapElement = this.element.querySelector('[data-controller*="dynamic-map"]');
    
    if (!mapElement) {
      console.warn("Dynamic map element not found");
      return;
    }

    // Get the controller from the element
    // Stimulus stores controllers on the element
    let mapController = null;
    
    // Try to find the controller by checking all registered controllers
    for (const controller of this.application.controllers) {
      if (controller.element === mapElement && 
          typeof controller.addPolylineFromCoordinates === "function") {
        mapController = controller;
        break;
      }
    }

    if (mapController && typeof mapController.addPolylineFromCoordinates === "function") {
      // Wait a bit for map to be ready if needed
      if (!mapController.bigMap) {
        if (this.retryCount < this.maxRetries) {
          this.retryCount++;
          setTimeout(() => {
            this.addPolylineToMap(coordinates);
          }, 500);
        }
        return;
      }
      mapController.addPolylineFromCoordinates(coordinates);
    } else {
      // Retry after a delay in case the map controller hasn't connected yet
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        setTimeout(() => {
          this.addPolylineToMap(coordinates);
        }, 1000);
      } else {
        console.warn("Failed to find dynamic map controller after retries");
      }
    }
  }
}
